import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { useDispatch, useSelector } from "react-redux";

import { RootState } from "../../store";
import { twoFactorAuthentication } from "../../services/login";
import { authActions } from "../../store/slices/AuthSlice";
import { BASE_URL, DASHBOARD_URL } from "../../Routes/urls";
import { warningActions } from "../../store/slices/WarningSlice";
import { getClasses } from "../../utils";
import PrimaryButton from "../../components/Buttons/PrimaryButton";
import classes from "./TwoFactorAuthentication.module.css";
import logoImage from "../../assets/cimpar-logo.svg";
import Card from "../../components/Card/Card";
import Modal from "../../components/Modal/Modal";
import LinkButton from "../../components/Buttons/LinkButton";

type Props = {
  onClose: () => void;
  email: string;
};

const otpLength = 6;

// The expiration time for the code, in seconds
const codeExpiredTime = 600;

const TwoFactorAuthentication: React.FC<Props> = ({ email, onClose }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const resourceName =
    useSelector((state: RootState) => state.auth.resourceName) || "";

  const [otp, setOtp] = useState(new Array(otpLength).fill(""));
  const otpBoxReference = useRef<(HTMLInputElement | null)[]>([]);
  const [fieldErrors, setFieldErrors] = useState<Boolean[]>(
    new Array(otpLength).fill(false)
  );
  const [timeLeft, setTimeLeft] = useState(codeExpiredTime);
  const [isBackspacePressed, setIsBackspacePressed] = useState<Boolean>(false);

  function handleChange(value: string, index: number) {
    let newArr = [...otp];
    newArr[index] = value.toUpperCase();
    setOtp(newArr);

    if (value && index < otpLength - 1) {
      otpBoxReference.current[index + 1]?.focus();
    }
    setFieldErrors(new Array(otpLength).fill(false));
    setIsBackspacePressed(false);
  }

  function handleBackspaceAndEnter(e: any, index: number) {
    if (e.key === "Backspace") {
      if (!isBackspacePressed) {
        let newArr = [...otp];
        newArr[index] = "";
        setOtp(newArr);
        setIsBackspacePressed(true);
      } else if (index > 0) {
        otpBoxReference.current[index - 1]?.focus();
        setIsBackspacePressed(false);
      }
    }
    if (e.key === "Enter" && e.target.value && index < otpLength - 1) {
      otpBoxReference.current[index + 1]?.focus();
    }
  }

  function handlePaste(event: ClipboardEvent) {
    const paste = event.clipboardData?.getData("text") || "";
    if (paste.length === otpLength) {
      setOtp(paste.split(""));
      otpBoxReference.current[paste.length - 1]?.focus();
    }
  }

  useEffect(() => {
    const firstInput = otpBoxReference.current[0];
    if (firstInput) {
      firstInput.addEventListener("paste", handlePaste);
    }
    return () => {
      if (firstInput) {
        firstInput.removeEventListener("paste", handlePaste);
      }
    };
  }, []);

  const getError = () => {
    otp.forEach((e, idx) => {
      if (e === "") {
        setFieldErrors((prevState) => {
          const newState = [...prevState];
          newState[idx] = true;
          return newState;
        });
      }
    });
    return otp.some((e) => e === "");
  };

  const verifyHandler = async () => {
    if (getError()) {
      return;
    }

    try {
      const res = await twoFactorAuthentication({
        email: email,
        otp: otp.join(""),
      });
      if (res.status === 200) {
        if (res.data.success) {
          localStorage.setItem("isLoggedIn", "true");
          dispatch(authActions.setAuthenticated());
          navigate(`${BASE_URL}/${resourceName}/${DASHBOARD_URL}`);
        } else {
          if (res.data.errorCode === "invalid_token") {
            dispatch(
              warningActions.showWarning({
                message: "Invalid code. Please provide the correct code.",
              })
            );
            setOtp(new Array(otpLength).fill(""));
          } else {
            dispatch(
              warningActions.showWarning({
                message: "Your code has expired. Please request login again.",
              })
            );
            setTimeLeft(0);
            setOtp(new Array(otpLength).fill(""));
          }
        }
      }
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    const startTime = Date.now();
    const interval = setInterval(() => {
      const elapsedTime = Date.now() - startTime;
      setTimeLeft(
        Math.max(0, codeExpiredTime - Math.floor(elapsedTime / 1000))
      );
      if (elapsedTime >= codeExpiredTime * 1000) {
        clearInterval(interval);
        onClose();
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [onClose]);

  const minutes = Math.floor(timeLeft / 60);
  const seconds = timeLeft % 60;
  const timer = `${minutes < 10 ? `0${minutes}` : minutes}:${
    seconds < 10 ? `0${seconds}` : seconds
  }`;

  return (
    <Modal>
      <div className={classes.main}>
        <Card className={classes.card}>
          <div className={classes.logoContainer}>
            <img src={logoImage} alt='CIMPAR' className={classes.logo} />
          </div>
          <div className={classes.heading}>Two-Factor Authentication</div>
          <div className={classes.description}>
            For enhanced security, please enter the authentication code sent to
            your registered email. Please re-initiate the login in case you do
            not receive the code.
          </div>
          <div className={classes.otpInputContainer}>
            {otp.map((digit, index) => (
              <input
                key={index}
                value={digit}
                maxLength={1}
                onChange={(e) => handleChange(e.target.value, index)}
                onKeyUp={(e) => handleBackspaceAndEnter(e, index)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    verifyHandler();
                  }
                }}
                ref={(reference) =>
                  (otpBoxReference.current[index] = reference)
                }
                className={getClasses(
                  classes.input,
                  fieldErrors[index] ? classes.error : ""
                )}
                autoFocus={0 === index}
              />
            ))}
          </div>
          <div className={classes.timerContainer}>
            <div>Code expires in {timer}</div>
          </div>
          <div className={classes.container}>
            <PrimaryButton
              name='VERIFY'
              className={classes.verifyBtn}
              onClick={verifyHandler}
            />
          </div>
          <div className={classes.container}>
            <LinkButton label='Back to login' onClick={onClose} />
          </div>
        </Card>
      </div>
    </Modal>
  );
};

export default TwoFactorAuthentication;
